// client/src/components/marketer/MarketerInfluencerStats.js
import React, { useState, useEffect, useRef, useCallback } from 'react';
import '../../styles/marketer/MarketerInfluencerStats.css';

const API_URL = process.env.REACT_APP_API_URL || '';

// ─── 상수 ────────────────────────────────────────────────

const CONTENT_TYPE_OPTIONS = [
  { platform: 'instagram', contentType: 'feed',   label: '인스타그램 피드' },
  { platform: 'instagram', contentType: 'reels',  label: '인스타그램 릴스' },
  { platform: 'blog',      contentType: 'post',   label: '블로그 포스팅' },
  { platform: 'youtube',   contentType: 'video',  label: '유튜브 영상 (롱폼)' },
  { platform: 'youtube',   contentType: 'shorts', label: '유튜브 쇼츠' },
  { platform: 'tiktok',    contentType: 'video',  label: '틱톡 영상' },
];

const FOLLOWER_RANGES = {
  blog: [
    { key: '0-20',      label: '20 이하' },
    { key: '21-100',    label: '21~100' },
    { key: '101-300',   label: '101~300' },
    { key: '301-1000',  label: '301~1,000' },
    { key: '1001-3000', label: '1,001~3,000' },
    { key: '3001-5000', label: '3,001~5,000' },
    { key: '5001+',     label: '5,001 이상' },
  ],
  instagram: [
    { key: '0-5000',       label: '5,000 이하' },
    { key: '5001-10000',   label: '5,001~10,000' },
    { key: '10001-20000',  label: '10,001~20,000' },
    { key: '20001-50000',  label: '20,001~50,000' },
    { key: '50001-100000', label: '50,001~100,000' },
    { key: '100001+',      label: '10만 이상' },
  ],
};
FOLLOWER_RANGES.youtube = FOLLOWER_RANGES.instagram.map(r => ({ ...r }));
FOLLOWER_RANGES.tiktok  = FOLLOWER_RANGES.instagram.map(r => ({ ...r }));

const CATEGORIES = [
  '뷰티/패션', '음식/맛집', '여행/라이프스타일', 'IT/테크',
  '피트니스/건강', '육아/교육', '반려동물', '기타',
];

// ─── 헬퍼 ────────────────────────────────────────────────

function formatWon(n) {
  if (n == null) return '-';
  return n.toLocaleString('ko-KR') + '원';
}

// 완결 문장 반환 — 메시지 템플릿에 조사 추가 금지
function buildFilterGuide(followerRange, category) {
  if (followerRange && category)   return '팔로워 구간을 조정하거나 카테고리를 바꾸면 더 많은 통계를 볼 수 있어요.';
  if (followerRange && !category)  return '팔로워 구간을 조정하면 더 많은 통계를 볼 수 있어요.';
  if (!followerRange && category)  return '카테고리를 바꾸면 더 많은 통계를 볼 수 있어요.';
  return '다른 광고 유형도 확인해보세요.';
}

// ─── 컴포넌트 ─────────────────────────────────────────────

function MarketerInfluencerStats() {
  const [selectedOption, setSelectedOption]         = useState(null); // { platform, contentType, label }
  const [selectedCategory, setSelectedCategory]     = useState('');
  const [selectedFollowerRange, setSelectedFollowerRange] = useState('');

  const [data, setData]       = useState(null);  // { overview, stats }
  const [loading, setLoading] = useState(false);
  const [error, setError]     = useState(false);

  const abortRef = useRef(null);

  // ── API 호출 ────────────────────────────────────────────
  const fetchStats = useCallback(async (option, category, followerRange) => {
    // 이전 요청 취소
    if (abortRef.current) abortRef.current.abort();
    const controller = new AbortController();
    abortRef.current = controller;

    setLoading(true);
    setError(false);

    try {
      const params = new URLSearchParams();
      if (option) {
        params.set('platform', option.platform);
        params.set('contentType', option.contentType);
      }
      if (category)      params.set('category', category);
      if (followerRange) params.set('followerRange', followerRange);

      const res = await fetch(
        `${API_URL}/api/marketer/mybiz/influencer-stats?${params}`,
        { credentials: 'include', signal: controller.signal }
      );
      // res.ok 선체크 — 502/HTML 응답 시 .json() 파싱 오류 방지
      if (!res.ok) {
        setError(true);
        return;
      }
      const json = await res.json();
      if (json.ok) {
        setData(json);
      } else {
        setError(true);
      }
    } catch (e) {
      if (e.name === 'AbortError') return; // 취소된 요청 — 에러 UI 없음
      setError(true);
    } finally {
      // 최신 요청만 loading 해제 — 이전 abort된 요청의 finally가 덮어쓰는 것을 방지
      if (abortRef.current === controller) setLoading(false);
    }
  }, []);

  // ── 마운트 시 overview 조회 ─────────────────────────────
  useEffect(() => {
    fetchStats(null, '', '');
    return () => { if (abortRef.current) abortRef.current.abort(); };
  }, [fetchStats]);

  // ── 필터 변경 시 재조회 ─────────────────────────────────
  useEffect(() => {
    if (!selectedOption) return; // 광고 유형 미선택 → 호출 안 함
    fetchStats(selectedOption, selectedCategory, selectedFollowerRange);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [selectedOption, selectedCategory, selectedFollowerRange]);

  // ── 광고 유형 칩 클릭 ──────────────────────────────────
  const handleOptionClick = (opt) => {
    if (selectedOption?.platform === opt.platform && selectedOption?.contentType === opt.contentType) {
      // 같은 칩 재클릭 → 선택 해제
      setSelectedOption(null);
      setSelectedCategory('');
      setSelectedFollowerRange('');
      fetchStats(null, '', ''); // overview만
      return;
    }
    // 플랫폼 변경 시 followerRange 초기화
    // ⚠️ instagram/youtube/tiktok이 동일한 구간을 공유하더라도 "플랫폼 전환 시 일괄 초기화" 정책으로 처리
    if (selectedOption?.platform !== opt.platform) {
      setSelectedFollowerRange('');
    }
    setSelectedOption(opt);
    // category는 유지 (플랫폼 변경해도 유지)
  };

  // ── 렌더: overview 카드 ────────────────────────────────
  // ⚠️ overview 에러 시 에러 상태를 overview 영역에서 직접 표시해야 함
  //    error && !data: overview 초기 조회 실패 → silent failure 방지
  const renderOverview = () => {
    const platforms = [
      { key: 'instagram', label: '인스타그램' },
      { key: 'blog',      label: '블로그' },
      { key: 'youtube',   label: '유튜브' },
      { key: 'tiktok',    label: '틱톡' },
    ];

    // 초기 overview 조회 실패 (data 없음 + error 상태)
    if (error && !data) {
      return (
        <div className="note note--red" style={{ marginTop: 0, marginBottom: 16, display: 'flex', alignItems: 'center', justifyContent: 'space-between', gap: 12, flexWrap: 'wrap' }}>
          <span>현황 데이터를 불러오지 못했어요.</span>
          <button
            className="mb-btn mb-btn-secondary mb-btn-sm"
            onClick={() => fetchStats(selectedOption, selectedCategory, selectedFollowerRange)}
          >
            다시 시도
          </button>
        </div>
      );
    }

    return (
      <>
        <div className="ovw" style={{ marginBottom: 8 }}>
          {platforms.map(p => {
            const info = data?.overview?.[p.key];
            return (
              <div key={p.key} className="o">
                <span className="platform">
                  <img src={`/images/sns/${p.key}.svg`} alt={p.label} />
                </span>
                <div>
                  <b>{p.label}</b>
                  <span>
                    {/* data=null + loading 중: 로딩 표시 / data 있음: 수치 표시 / 그 외: "—" */}
                    {!data && loading
                      ? '로딩 중...'
                      : info
                        ? `전체 ${info.total.toLocaleString()}명 · 단가 공개 ${info.rated.toLocaleString()}명`
                        : '—'}
                  </span>
                </div>
              </div>
            );
          })}
        </div>
        <p className="t-sub" style={{ margin: '0 2px 16px' }}>참여 중(활성) 승인 계정 수 (UI 필터 무관)</p>
      </>
    );
  };

  // ── 렌더: 통계 결과 ────────────────────────────────────
  const renderStats = () => {
    const stats = data?.stats;

    // 광고 유형 미선택
    if (!selectedOption) {
      return (
        <div className="note note--blue" style={{ marginTop: 0 }}>
          광고 유형을 선택하면 단가 통계를 볼 수 있어요.
        </div>
      );
    }

    // 에러
    if (error) {
      return (
        <div className="note note--red" style={{ marginTop: 0, display: 'flex', alignItems: 'center', justifyContent: 'space-between', gap: 12, flexWrap: 'wrap' }}>
          <span>데이터를 불러오는 중 문제가 생겼어요. 잠시 후 다시 시도해주세요.</span>
          <button
            className="mb-btn mb-btn-secondary mb-btn-sm"
            onClick={() => fetchStats(selectedOption, selectedCategory, selectedFollowerRange)}
          >
            다시 시도
          </button>
        </div>
      );
    }

    // 로딩 (이전 결과 dim 처리)
    const wrapperClass = loading ? 'mis-stats-wrapper loading' : 'mis-stats-wrapper';

    // 첫 조회 로딩 중 (stats 없음 + loading 중) — 스피너만 표시
    // ⚠️ 이 분기가 없으면 칩 첫 클릭마다 "결과 없음" 문구가 잠깐 노출됨
    if (!stats && loading) {
      return (
        <div className="mis-stats-wrapper">
          <div className="mis-loading-overlay" style={{ position: 'relative', height: '120px' }}>
            <div className="mis-spinner" />
          </div>
        </div>
      );
    }

    // stats=null이고 로딩도 아닌 경우 — 서버 정책상 정상 흐름에서 도달 불가 (방어 코드)
    if (!stats) {
      return (
        <div className="mis-stats-wrapper">
          <div className="note note--blue" style={{ marginTop: 0 }}>선택한 조합의 결과가 없어요.</div>
        </div>
      );
    }

    // 결과 헤더
    const headerParts = [selectedOption.label];
    if (selectedCategory) headerParts.push(selectedCategory);
    if (selectedFollowerRange) {
      const rangeLabel = (FOLLOWER_RANGES[selectedOption.platform] || [])
        .find(r => r.key === selectedFollowerRange)?.label;
      if (rangeLabel) headerParts.push(rangeLabel);
    }
    // ⚠️ followerSectionLabel은 이 함수 내에서 사용하지 않는다.
    //    컴포넌트 레벨(return JSX 안)의 같은 이름 변수를 사용할 것.
    //    여기서 재선언하지 말 것 — lint no-unused-vars 빌드 실패 원인

    const guideText = buildFilterGuide(selectedFollowerRange, selectedCategory);

    // 결과 헤더 행 (시안: 제목 + 표본 메타) — rateUnavailable에서도 totalCount 표시, 다른 분기와 일관성 유지
    const headerRow = (
      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'baseline', gap: 10, flexWrap: 'wrap', marginBottom: 14 }}>
        <h3 style={{ margin: 0, fontSize: '15.5px', fontWeight: 800, color: '#0b1324' }}>{headerParts.join(' · ')}</h3>
        <span className="t-sub">전체 {stats.totalCount.toLocaleString()}명 · 단가 공개 {stats.ratedCount.toLocaleString()}명</span>
      </div>
    );
    const loadingOverlay = loading && <div className="mis-loading-overlay"><div className="mis-spinner" /></div>;

    // ratedCount=0
    if (stats.rateUnavailable) {
      // overview.rated: 이 플랫폼 전체 단가 공개 수 (필터 무관) — #27 권장 안내
      const platformRated = data?.overview?.[selectedOption.platform]?.rated || 0;
      return (
        <div className={wrapperClass}>
          {loadingOverlay}
          {headerRow}
          <div className="note note--blue" style={{ marginTop: 0 }}>
            <p style={{ margin: 0 }}>아직 이 조건에 단가를 공개한 인플루언서가 없어요.</p>
            <p style={{ margin: '4px 0 0' }}>
              셀프애드에 참여하는 인플루언서가 꾸준히 늘고 있어요.
              {platformRated > 0 && ` 현재 이 플랫폼엔 단가를 공개한 인플루언서가 ${platformRated}명 있어요.`}
              {' '}{guideText}
            </p>
          </div>
        </div>
      );
    }

    // ratedCount 1~2
    if (stats.minMaxUnavailable) {
      return (
        <div className={wrapperClass}>
          {loadingOverlay}
          {headerRow}
          <div className="note note--blue" style={{ marginTop: 0 }}>
            단가 공개 인플루언서가 아직 소수예요. {guideText}
          </div>
        </div>
      );
    }

    // ratedCount 3~4: min/max 표시, avg 불가
    if (stats.avgRateUnavailable) {
      return (
        <div className={wrapperClass}>
          {loadingOverlay}
          {headerRow}
          <div className="rate3">
            <div className="r">
              <span>최저 단가</span>
              <b>{formatWon(stats.minRate)}</b>
            </div>
            <div className="r">
              <span>최고 단가</span>
              <b>{formatWon(stats.maxRate)}</b>
            </div>
            <div className="r is-avg">
              <span>평균 단가</span>
              <div style={{ fontSize: '12.5px', color: '#3b4658', fontWeight: 600, lineHeight: 1.5 }}>
                평균을 계산하기엔 아직 데이터가 충분하지 않아요. {guideText}
              </div>
            </div>
          </div>
        </div>
      );
    }

    // ratedCount 5~9: 전체 표시 + 캐비앳
    // ratedCount≥10: 정상 표시
    const showCaveat = stats.ratedCount < 10;

    // A안: 평균을 히어로 숫자로, 최저~최고를 범위 바로 (avg 마커 위치는 범위 내 비율)
    const range = Math.max(1, (stats.maxRate || 0) - (stats.minRate || 0));
    const avgPos = Math.min(96, Math.max(4, ((stats.avgRate - stats.minRate) / range) * 100));

    return (
      <div className={wrapperClass}>
        {loadingOverlay}
        {headerRow}
        <div className="mbh-rate" style={{ boxShadow: 'none', padding: '6px 0 0', marginBottom: 0 }}>
          <div className="mbh-rate__label">평균 단가</div>
          <div className="mbh-rate__num">{formatWon(stats.avgRate)}</div>
          <div className="mbh-range">
            <div className="mbh-range__bar"><span className="mbh-range__avg" style={{ left: `${avgPos}%` }} /></div>
            <div className="mbh-range__ends">
              <span>최저<b>{formatWon(stats.minRate)}</b></span>
              <span style={{ textAlign: 'right' }}>최고<b>{formatWon(stats.maxRate)}</b></span>
            </div>
          </div>
          {showCaveat && (
            <div style={{ fontSize: '12px', color: '#6b7686', fontWeight: 600, marginTop: 12, lineHeight: 1.5 }}>
              표본 {stats.ratedCount}명 · 이상치에 민감한 구간이니 최저~최고 범위도 함께 참고해주세요
            </div>
          )}
        </div>
      </div>
    );
  };

  // ── 현재 플랫폼의 팔로워 구간 목록 ─────────────────────
  const currentFollowerRanges = selectedOption
    ? (FOLLOWER_RANGES[selectedOption.platform] || [])
    : [];
  const followerSectionLabel = selectedOption?.platform === 'blog'
    ? '일일 방문자 수 구간'
    : '팔로워 수 구간';

  return (
    <div>
      <div className="mb-page-header">
        <h2 className="mb-page-title">인플루언서 단가 통계</h2>
        <p className="mb-page-description">
          광고 유형·분야·팔로워 구간별 인플루언서 단가 현황을 확인하세요.
        </p>
      </div>

      {/* Overview 카드 */}
      {renderOverview()}

      {/* 필터 영역 */}
      <div className="mb-card" style={{ marginBottom: 16 }}>
        {/* 광고 유형 칩 */}
        <h3 style={{ margin: '0 0 12px', fontSize: '15.5px', fontWeight: 800, color: '#0b1324' }}>광고 유형</h3>
        <div className="chips" style={{ marginBottom: 16 }}>
          {CONTENT_TYPE_OPTIONS.map(opt => {
            const isActive = selectedOption?.platform === opt.platform &&
                             selectedOption?.contentType === opt.contentType;
            return (
              <button
                key={`${opt.platform}-${opt.contentType}`}
                className={`chip${isActive ? ' is-active' : ''}`}
                onClick={() => handleOptionClick(opt)}
              >
                {opt.label}
              </button>
            );
          })}
        </div>

        <div className="mb-grid-2">
          {/* 카테고리 드롭다운 */}
          <div>
            <div style={{ fontSize: '13.5px', fontWeight: 700, color: '#3b4658', marginBottom: 8 }}>광고 분야</div>
            <select
              className="mb-form-control"
              value={selectedCategory}
              onChange={e => setSelectedCategory(e.target.value)}
              disabled={!selectedOption}
            >
              <option value="">
                {selectedOption ? '전체 분야' : '광고 유형을 먼저 선택해주세요'}
              </option>
              {CATEGORIES.map(c => (
                <option key={c} value={c}>{c}</option>
              ))}
            </select>
          </div>

          {/* 팔로워 구간 (광고 유형 선택 시에만 표시) */}
          {selectedOption && currentFollowerRanges.length > 0 && (
            <div>
              <div style={{ fontSize: '13.5px', fontWeight: 700, color: '#3b4658', marginBottom: 8 }}>{followerSectionLabel}</div>
              <div className="chips" style={{ margin: 0 }}>
                {currentFollowerRanges.map(r => (
                  <button
                    key={r.key}
                    className={`chip${selectedFollowerRange === r.key ? ' is-active' : ''}`}
                    onClick={() => setSelectedFollowerRange(
                      selectedFollowerRange === r.key ? '' : r.key
                    )}
                  >
                    {r.label}
                  </button>
                ))}
              </div>
            </div>
          )}
        </div>
      </div>

      {/* 통계 결과 */}
      <div className="mb-card">
        {renderStats()}
      </div>
    </div>
  );
}

export default MarketerInfluencerStats;
